import * as React from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import * as uuid from 'uuid';
import * as classnames from 'classnames';
import { interpolate, parseId } from '../../../utils/string';
import Select, { Creatable } from 'react-select';

import Loader from '../../common/Loader/Loader';
import NotifyHOC from '../../../HOC/Notifications';

import { EDIT_NEW_PROJECT } from '../../../constants/routes';
import {
    PanelType,
    Project,
    ProjectID,
    PROJECT_EDIT_STATUS,
    PROJECT_STATUS,
    TENANT_LEVEL,
    VIEW_MODE
} from '@nexxt/common/types';
import {
    INCA_LOGO_URL,
    RESEARCH_DOMAIN_OPTIONS,
    projectLanguage
} from '@nexxt/common/constants';
import * as overview from '../../../mapping/overview';

import * as tokenService from '@nexxt/common/firebase/auth/userDetails';
import * as clientService from '../../../services/client.service';
import * as projectService from '../../../services/project.service';
import * as Action from '../../../actions/project.actions';
import { alertMessage } from '../../../constants/alertMessage';
import * as _ from 'lodash';
import { PROJECT_EDIT_MODES } from '../../../interface/project.interface';
import { ILocation } from '../../../interface/common.interface';
import { IApplicationState } from '../../../reducers';
import { getPrice } from '../../../services/payment.service';
import {
    clearEditedStatus,
    setEditedStatus
} from '../../../services/utils.service';
import config from '../../../config';
import CheckboxMultiSelect from '../../common/reactSelect/CheckboxMultiSelect';
import { DISABLED_EDIT_MODES, LOCK_PROJECT } from '../../../constants/project';
import { questionErrorMessage } from '../../../constants/applicationMessage';
import AutoSavingService from '../../../utils/AutoSavingService';
import AddClient from './AddClient';
import HelperTooltip from '../../common/HelperTooltip';
import ValidationService from '@nexxt/common/services/ValidationService';
import { defaultPanelSource } from '../Audience/defaultValues';
import BotAvatar from '../botSettings/BotAvatar';
interface OverviewProps {
    projectHealth: any;
    location: ILocation;
    currentProject: Project;
    toastNotification: any;
    editStatus: PROJECT_EDIT_STATUS;
    editMode: PROJECT_EDIT_MODES;
    editEnabled: boolean;
    viewMode: VIEW_MODE;
    setProject: (newProject: Project) => void;
    updateProject: (projectID: ProjectID, project: Project) => Promise<any>;
    checkProjectHealth: (projectID: number) => Promise<any>;
    getPrice: (data: any) => Promise<any>;
    notify: (
        msg: string,
        type: string,
        options?: Record<string, unknown>,
        callback?: unknown
    ) => any;
    changePath: (path: string) => void;
    setEditedStatus: (
        type: string,
        status: PROJECT_EDIT_STATUS
    ) => Promise<any>;
    clearEditedStatus: (status?: PROJECT_EDIT_STATUS) => Promise<any>;
}

interface OverviewState {
    isRequesting: boolean;
    project: any;
    clientList: any[];
    errorObject: any;
    isMediaLibraryOpen: boolean;
    isAddClientOpen: boolean;
    newClient: any;
    templateSettings: any[];
    autoSavingService: AutoSavingService<Project>;
    clientTerm: 'Client' | 'Team/Program';
    researchDomainOptions: {
        label: string;
        value: string;
        created?: boolean;
    }[];
}

class Overview extends React.Component<OverviewProps, OverviewState> {
    private validationService;
    constructor(props: OverviewProps) {
        super(props);
        this.validationService = new ValidationService('message');
        this.state = {
            isRequesting: false,
            project: null,
            clientList: [],
            errorObject: {},
            isMediaLibraryOpen: false,
            isAddClientOpen: false,
            newClient: { clientName: '' },
            templateSettings: null,
            autoSavingService: null,
            clientTerm: 'Client',
            researchDomainOptions: RESEARCH_DOMAIN_OPTIONS
        };
    }

    async componentDidMount(): Promise<void> {
        if (tokenService.getTenantLevel() === TENANT_LEVEL.SELF_SERVICE) {
            this.setState({ clientTerm: 'Team/Program' });
        }
        await this.getClients();

        await this.getProjectById();
        this.getTemplateSettings();
    }

    componentDidUpdate(
        prevProps: Readonly<OverviewProps>,
        prevState: Readonly<OverviewState>,
        snapshot?: any
    ): void {
        this.state.autoSavingService?.setData(this.state.project);
        if (!_.isEqual(prevProps.editStatus, this.props.editStatus)) {
            this.state.autoSavingService?.setCurrentEditStatus(
                this.props.editStatus
            );
        }
        if (prevState.isRequesting !== this.state.isRequesting) {
            this.state.autoSavingService?.setIsSaving(this.state.isRequesting);
        }
    }

    componentWillUnmount(): void {
        this.state.autoSavingService &&
            this.state.autoSavingService.clearAutoSaving();
    }
    getTemplateSettings = () => {
        const { editMode } = this.props;
        const { project } = this.state;
        try {
            const templateSettings =
                LOCK_PROJECT[editMode as PROJECT_EDIT_MODES][
                    project?.templateCode
                ]?.project;

            this.setState({ templateSettings });
        } catch (err) {}
    };

    getProjectById = async () => {
        try {
            this.setState({ isRequesting: true });
            const projectID = parseId(this.props.location.pathname);
            if (projectID === 'new') {
                if (!this.state.clientList.length) {
                    await this.createClient(
                        {
                            clientName: 'Internal'
                        },
                        INCA_LOGO_URL
                    );
                }

                const tenantID = tokenService.getTenantId();
                const initialProject = {
                    name: 'Build Your Own',
                    codeName: 'BYO_' + uuid.v4(),
                    languages: [projectLanguage[0]],
                    devices: overview.devices,
                    client: this.state.clientList[0],
                    clientLogo: '',
                    tenantID,
                    sessionTimeout: 2,
                    redirectURL: 'https://nexxt.in',
                    redirectURLQuotafull: 'https://nexxt.in',
                    redirectURLTermination: 'https://nexxt.in',
                    title: 'inca',
                    botTheme: 'white',
                    botSpeed: 'fast',
                    avatarImage: INCA_LOGO_URL,
                    panelSettings: {
                        panelType: PanelType.SELF_PROVIDED,
                        panels: [defaultPanelSource]
                    },
                    status: PROJECT_STATUS.DRAFT,
                    disqualification:
                        this.props.editMode === PROJECT_EDIT_MODES.TEMPLATE_MODE
                            ? overview.disqualificationSelfServiceMapping[0]
                            : overview.disqualificationFullServiceMapping[0],
                    discoursePolicy: {
                        persona: overview.discoursePersonasMapping[0]
                    },
                    surveyTopics: [],
                    researchCategory: null
                };

                this.setState(
                    {
                        project: initialProject
                    },
                    async () => await this.handleSubmit()
                );
                return;
            }
            const response = await projectService.fetchProject(projectID);
            const project = {
                ...response.data.data,
                packageID: response.data.packageID
            };
            // this.initialProject = project;
            await this.props.setProject(project);
            const formattedProject = {
                ...project,
                devices: (project.devices || []).map(
                    (d) => overview.deviceMapping[d]
                ),
                discoursePolicy: {
                    persona: overview.discoursePersonasMapping.find(
                        (mapping) =>
                            mapping.value === project?.discoursePolicy?.persona
                    )
                }
            };

            const customSurveyTopics = project.surveyTopics?.filter(
                (topic) => topic?.created
            );
            this.setState(
                {
                    project: formattedProject,
                    isRequesting: false,
                    autoSavingService: new AutoSavingService(
                        formattedProject,
                        'project | overview',
                        this.state.isRequesting,
                        this.props.editStatus,
                        this.props.setEditedStatus,
                        this.props.clearEditedStatus
                    ),
                    researchDomainOptions: [
                        ...this.state.researchDomainOptions,
                        ...(customSurveyTopics || [])
                    ]
                },
                () =>
                    this.state.autoSavingService.setAutoSavingInterval(
                        this.handleSubmit
                    )
            );
            await this.props.checkProjectHealth(projectID);
            await this.props.getPrice({ projectID });
            this.setEditFlag(true);
        } catch (err) {
            this.setState({ isRequesting: false });
            this.props.toastNotification.error(
                alertMessage.UNABLE_FETCH_PROJECT
            );
        }
    };

    createClient = async (values, clientURL) => {
        if (!values.clientName) {
            this.props.toastNotification.info(
                _.template(alertMessage.CREATE_NEW_CLIENT_t)({
                    clientTerm: this.state.clientTerm
                })
            );
        } else if (typeof values.clientName !== 'string') {
            this.props.toastNotification.info(
                _.template(alertMessage.CREATE_NEW_CLIENT_t)({
                    clientTerm: this.state.clientTerm
                })
            );
        } else {
            this.setState({
                isRequesting: true
            });

            const { clientName } = values;
            const tenantID = tokenService.getTenantId();
            const data = {
                clientName: clientName,
                clientLogo: clientURL,
                tenantID
            };
            try {
                await clientService.createClient(data);
                this.setState(
                    {
                        isRequesting: false
                    },
                    () => {
                        this.props.toastNotification.success(
                            `${this.state.clientTerm} created successfully!`
                        );
                    }
                );
                await this.getClients();

                this.setState({
                    isAddClientOpen: false
                });
            } catch (error) {
                this.setState({
                    isRequesting: false
                });
                this.props.toastNotification.error(error.response.data.message);
            }
        }
    };

    getClients = async () => {
        const tenantId = tokenService.getTenantId();
        this.setState({ isRequesting: true });

        try {
            const response = await clientService.fetchClients(tenantId);

            this.setState({
                clientList: response.data.data,
                isRequesting: false
            });
        } catch (err) {
            this.setState({ isRequesting: false });
        }
    };

    setProjectValue = (keyName: string, value) => {
        this.setState(
            (prevState) => ({
                ...prevState,
                project: { ...(prevState.project as Project), [keyName]: value }
            }),
            this.setEditFlag
        );
    };

    handleCreateResearchDomain = (inputValue: string) => {
        if (inputValue.length > 20) {
            this.setState((prevState) => ({
                errorObject: {
                    ...prevState.errorObject,
                    isError: true,
                    surveyTopics:
                        questionErrorMessage.RESEARCH_DOMAIN_LENGTH_LIMIT
                }
            }));

            return;
        }
        const newOption = {
            label: inputValue,
            value: inputValue,
            created: true
        };
        this.setState((prevState) => ({
            researchDomainOptions: [
                ...prevState.researchDomainOptions,
                newOption
            ]
        }));

        this.setProjectValue('surveyTopics', [newOption]);
    };

    disableInputByFieldName = (fieldName: string): boolean => {
        const { editEnabled, editMode } = this.props;
        const { templateSettings } = this.state;

        const lockMode = !editEnabled
            ? templateSettings || ['all']
            : editMode === PROJECT_EDIT_MODES.REPUBLISH
            ? [
                  'projectName',
                  'client',
                  'projectLanguage',
                  'devices',
                  'disqualification',
                  'personas'
              ]
            : [];

        return lockMode?.length ? !lockMode.includes(fieldName) : false;
    };

    isValid = () => {
        const { project } = this.state;
        const { isEmpty, containsSpecialCharacters } = this.validationService;
        let errorObject: any = { isError: false };

        if (isEmpty(project.name)) {
            errorObject = {
                ...errorObject,
                isError: true,
                name: questionErrorMessage.REQUIRED
            };
        }

        if (isEmpty(project.codeName)) {
            errorObject = {
                ...errorObject,
                isError: true,
                codeName: questionErrorMessage.REQUIRED
            };
        }

        if (containsSpecialCharacters(project.codeName)) {
            errorObject = {
                ...errorObject,
                isError: true,
                codeName: questionErrorMessage.SPECIAL_CHARACTERS
            };
        }

        if (!project.languages?.length) {
            errorObject = {
                ...errorObject,
                isError: true,
                languages: questionErrorMessage.REQUIRED
            };
        }

        if (!project.devices.length) {
            errorObject = {
                ...errorObject,
                isError: true,
                devices: questionErrorMessage.REQUIRED
            };
        }

        if (isEmpty(project.disqualification?.value)) {
            errorObject = {
                ...errorObject,
                isError: true,
                disqualification: questionErrorMessage.REQUIRED
            };
        }

        this.setState({ errorObject });
        return errorObject;
    };

    setEditFlag = (init = false) => {
        !init && this.state.autoSavingService.setEditing();
        const errorObject = this.isValid();
        this.state.autoSavingService.setEditFlag(errorObject);
    };
    handleSubmit = async () => {
        const project = _.cloneDeep(this.state.project);
        try {
            const projectId = parseId(this.props.location.pathname);
            const formatProject = {
                ...project,
                devices: project.devices.map((d) => d.value),
                client: {
                    ...project.client,
                    value: project.client.clientID
                        ? project.client.clientID
                        : project.client.value
                },
                discoursePolicy: {
                    persona: project.discoursePolicy.persona.value
                }
            };
            if (projectId === 'new') {
                const resp = await projectService.createProject(formatProject);
                const projectId = resp.data.data.projectId;

                this.props.changePath(
                    interpolate(EDIT_NEW_PROJECT, {
                        projectId
                    })
                );
                await this.getProjectById();
            } else {
                await this.props.updateProject(projectId, formatProject);
                this.state.autoSavingService.setSyncedData(project);
                this.props.setProject(formatProject);

                this.props.clearEditedStatus(
                    this.state.errorObject?.isError
                        ? PROJECT_EDIT_STATUS.EDIT_REQUIRED
                        : PROJECT_EDIT_STATUS.COMPLETED
                );
            }
        } catch (err) {
            console.error(err);
            this.props.toastNotification.error(err.response.data.message);
            this.props.clearEditedStatus(PROJECT_EDIT_STATUS.FAILED);
        }
    };
    setSelectedMedia = (newMedia) => {
        this.setProjectValue('avatarImage', newMedia.src);
    };

    render() {
        const {
            project,
            isRequesting,
            clientList,
            isAddClientOpen,
            newClient,
            errorObject,
            researchDomainOptions
        } = this.state;
        const { editMode, viewMode, editEnabled } = this.props;
        const isDevAccount = viewMode === VIEW_MODE.DEV;
        if (isRequesting || !project) {
            return <Loader isInline={false} />;
        }
        return (
            <div
                className="project-add--overview"
                style={{ position: 'relative' }}
            >
                <div className="form-block">
                    <div className="row">
                        <div
                            className={classnames(
                                'form-group form-group--inline-block col-md-6',
                                { error: errorObject?.name }
                            )}
                        >
                            <label className="form-group__label from-group--block">
                                Project name
                            </label>
                            <input
                                className="form-group__control"
                                type="text"
                                name={'name'}
                                value={this.state.project?.name}
                                onChange={(e) =>
                                    this.setProjectValue(
                                        e.target.name,
                                        e.target.value
                                    )
                                }
                                disabled={this.disableInputByFieldName(
                                    'projectName'
                                )}
                            />
                            {errorObject?.name && (
                                <span className="form-group__error-msg">
                                    {errorObject.name}
                                </span>
                            )}
                        </div>
                        <div
                            className={classnames(
                                'form-group form-group--inline-block col-md-6',
                                { error: errorObject?.codeName }
                            )}
                        >
                            {' '}
                            <HelperTooltip
                                pathname="Project URL"
                                placement="bottom"
                                textSize="small"
                                icon={true}
                            >
                                <label className="form-group__label form-group__label--block">
                                    Project URL:
                                </label>
                            </HelperTooltip>
                            <div className="input-group">
                                <div className="input-group-append">
                                    <span
                                        className="input-group-text"
                                        id="basic-addon2"
                                    >
                                        {`${tokenService.getTenantUrl()}.${
                                            config.host.domain
                                        }/p/`}
                                    </span>
                                </div>
                                <input
                                    type="text"
                                    className={classnames(
                                        'form-group__control',
                                        {
                                            error: ''
                                        }
                                    )}
                                    aria-label="Enter text"
                                    aria-describedby="basic-addon2"
                                    name="codeName"
                                    placeholder="Project url"
                                    onChange={(e) =>
                                        this.setProjectValue(
                                            e.target.name,
                                            e.target.value
                                        )
                                    }
                                    value={this.state.project.codeName}
                                    disabled={this.disableInputByFieldName(
                                        'codeName'
                                    )}
                                />
                            </div>
                            {errorObject?.codeName && (
                                <span className="form-group__error-msg">
                                    {errorObject.codeName}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="row">
                        <div className="form-group col-md-6">
                            <AddClient
                                clientList={clientList}
                                project={project}
                                isAddClientOpen={isAddClientOpen}
                                client={newClient}
                                errorObject={errorObject}
                                editEnabled={editEnabled}
                                updateClient={(client) =>
                                    this.setState({ newClient: client })
                                }
                                setProjectValue={this.setProjectValue}
                                toggleAddClient={() =>
                                    this.setState((prevState) => ({
                                        isAddClientOpen:
                                            !prevState.isAddClientOpen
                                    }))
                                }
                                createClient={this.createClient}
                                disableInputByFieldName={
                                    this.disableInputByFieldName
                                }
                                clientTerm={this.state.clientTerm}
                            />
                        </div>
                        <div
                            className={classnames(
                                'form-group form-group--inline-block col-md-6',
                                {
                                    error: errorObject?.devices
                                }
                            )}
                        >
                            <HelperTooltip
                                pathname="Devices"
                                placement="bottom"
                                textSize="small"
                                icon={true}
                            >
                                <label className="form-group__label form-group__label--block">
                                    Devices
                                </label>
                            </HelperTooltip>
                            <CheckboxMultiSelect
                                options={overview.devices}
                                disabled={this.disableInputByFieldName(
                                    'devices'
                                )}
                                value={this.state.project.devices}
                                onChangeCallback={(response) =>
                                    this.setProjectValue('devices', response)
                                }
                            />
                        </div>
                        <div className="form-group col-md-6">
                            <label className="form-group__label form-group__label--block">
                                Bot Interviewer Avatar
                            </label>

                            <BotAvatar
                                setSelectedMedia={this.setSelectedMedia}
                                avatarImage={this.state.project.avatarImage}
                                disabled={this.disableInputByFieldName(
                                    'avatarImage'
                                )}
                            />
                            <p className="mb-0">
                                To customize the persona of inca SmartProbe AI
                                and for other controls, check out the advanced
                                Conversational Settings tab on any Open Ended
                                question.
                            </p>
                        </div>
                        <div className="form-group col-md-6"></div>
                        <div className="form-group col-md-6">
                            <HelperTooltip
                                pathname="Disqualification Control"
                                placement="bottom"
                                textSize="small"
                                icon={true}
                            >
                                <label
                                    className={classnames(
                                        'form-group__label form-group__label--block',
                                        {
                                            error: errorObject?.disqualification
                                        }
                                    )}
                                >
                                    Disqualification control
                                </label>
                            </HelperTooltip>
                            <p>
                                This setting controls how many demerit points a
                                participant may accrue before being
                                disqualified.
                            </p>
                            <Select
                                options={
                                    editMode ===
                                    PROJECT_EDIT_MODES.TEMPLATE_MODE
                                        ? overview.disqualificationSelfServiceMapping
                                        : overview.disqualificationFullServiceMapping
                                }
                                isDisabled={this.disableInputByFieldName(
                                    'disqualification'
                                )}
                                value={project.disqualification}
                                onChange={(response) =>
                                    this.setProjectValue(
                                        'disqualification',
                                        response
                                    )
                                }
                            />
                        </div>
                        <div
                            className={classnames(
                                'form-group form-group--inline-block col-md-6',
                                {
                                    error: errorObject?.languages
                                }
                            )}
                        >
                            {' '}
                            <HelperTooltip
                                pathname="Project Language"
                                placement="bottom"
                                textSize="small"
                                icon={true}
                            >
                                <label className="form-group__label form-group__label--block">
                                    Project Language
                                </label>
                            </HelperTooltip>
                            <CheckboxMultiSelect
                                options={projectLanguage}
                                disabled={this.disableInputByFieldName(
                                    'projectLanguage'
                                )}
                                value={this.state.project.languages}
                                onChangeCallback={(response) =>
                                    this.setProjectValue('languages', response)
                                }
                            />
                            {this.state.project.languages.length > 1 && (
                                <p className="mt-1 mb-0">
                                    {`You can import or export translation files to
                                quickly update all the question and option text
                                in your project by clicking the "More" button on
                                the top right of your page.`}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (store: IApplicationState) => {
    const {
        projectReducer: { currentProject, editEnabled, editMode },
        utilsReducer: { editStatus, viewMode }
    } = store;

    return {
        currentProject,
        editStatus,
        editEnabled: editEnabled['overview'],
        editMode,
        viewMode
    };
};

const mapDispatchToProps = (dispatch) => ({
    setProject: (newProject: Project) => {
        dispatch(Action.setProject(newProject));
    },
    updateProject: (projectID: ProjectID, project: Project) =>
        dispatch(projectService.updateProject(projectID, project)),
    checkProjectHealth: (projectID: number) =>
        dispatch(projectService.checkProjectHealth(projectID)),
    getPrice: (data) => dispatch(getPrice(data)),
    changePath: (path) => {
        dispatch(push(path));
    },
    setEditedStatus: (type: string, status: PROJECT_EDIT_STATUS) =>
        dispatch(setEditedStatus(type, status)),
    clearEditedStatus: (status?: PROJECT_EDIT_STATUS) =>
        dispatch(clearEditedStatus(status))
});

export default NotifyHOC(
    connect(mapStateToProps, mapDispatchToProps)(Overview)
);
